/** @fileoverview Queues prompts and dispatches them through persistent conversation chats. */

import type { Chat as AIChat } from '@ai-sdk/react';
import { useCallback, useRef, useState } from 'react';
import { requestAssistantSteer } from '../services/chatApi';
import { useChatStore } from '../stores/chatStore';
import type {
  ChatAttachment,
  Conversation,
  DispatchingChatMessage,
  FabrikUIMessage,
  QueuedChatMessage,
} from '../types/chat';
import type { ChatCallbacks } from './types';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '../lib/attachments';
import { aggregateSelection, type PreviewSelectionItem } from '../lib/previewSelection';

type PendingPrompt = QueuedChatMessage & {
  model: string;
  appId?: string;
  planMode?: boolean;
  callbacks: ChatCallbacks;
  selection?: PreviewSelectionItem[];
  attachments?: ChatAttachment[];
};

type MakeChat = (
  conversation: Conversation,
  initial?: FabrikUIMessage[],
  callbacks?: ChatCallbacks
) => AIChat<FabrikUIMessage>;

type RetryState = { attempt: number; maxRetries: number; failed?: boolean };

export const DEFAULT_MAX_CHAT_RETRIES = 3;
const STREAM_START_TIMEOUT_MS = 25_000;

class StreamStartTimeoutError extends Error {
  readonly status = 408;

  constructor() {
    super('The assistant did not start responding in time. Please try again.');
    this.name = 'StreamStartTimeoutError';
  }
}

const runChatRequest = async (chat: AIChat<FabrikUIMessage>, send: () => Promise<void>) => {
  const request = send();
  let unsubscribe = () => {};
  let timeout = 0;
  const startedOrFinished = new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: StreamStartTimeoutError) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      unsubscribe();
      if (error) reject(error);
      else resolve();
    };

    if (chat.status !== 'submitted') return resolve();
    timeout = window.setTimeout(
      () => finish(new StreamStartTimeoutError()),
      STREAM_START_TIMEOUT_MS
    );
    unsubscribe = chat['~registerStatusCallback'](() => {
      if (chat.status !== 'submitted') finish();
    });
    request.then(
      () => finish(),
      () => finish()
    );
    if (chat.status !== 'submitted') finish();
  });

  try {
    await Promise.race([request, startedOrFinished]);
    await request;
  } catch (error) {
    if (error instanceof StreamStartTimeoutError) {
      await chat.stop();
      await request.catch(() => undefined);
    }
    throw error;
  }
};

const isRetryableError = (error: unknown) => {
  if (!error || typeof error !== 'object') return true;
  const status = (error as { status?: unknown }).status;
  return typeof status !== 'number' || status >= 500;
};

export function useChatQueue(
  makeChat: MakeChat,
  getChat: (conversationId: string) => AIChat<FabrikUIMessage> | undefined,
  lifecycleGeneration: { current: number },
  selectedId: string | undefined,
  setSelectedChat: (chat: AIChat<FabrikUIMessage> | undefined) => void,
  maxRetries = DEFAULT_MAX_CHAT_RETRIES
) {
  const [queuedByConversation, setQueuedByConversation] = useState(
    new Map<string, QueuedChatMessage[]>()
  );
  const [dispatchingByConversation, setDispatchingByConversation] = useState(
    new Map<string, DispatchingChatMessage>()
  );
  const [retryByConversation, setRetryByConversation] = useState(new Map<string, RetryState>());
  const pendingPrompts = useRef(new Map<string, PendingPrompt[]>());
  const failedPrompts = useRef(new Map<string, PendingPrompt>());
  const cancelledRetries = useRef(new Set<string>());
  const retryLimit = Number.isFinite(maxRetries)
    ? Math.max(0, Math.floor(maxRetries))
    : DEFAULT_MAX_CHAT_RETRIES;
  const updateRetry = useCallback((id: string, retry?: RetryState) => {
    setRetryByConversation((current) => {
      const next = new Map(current);
      if (retry) next.set(id, retry);
      else next.delete(id);
      return next;
    });
  }, []);
  const runningPrompts = useRef(new Set<string>());

  const refreshQueuedMessages = useCallback(() => {
    const next = new Map<string, QueuedChatMessage[]>();
    for (const [id, prompts] of pendingPrompts.current) {
      if (prompts.length) {
        next.set(
          id,
          prompts.map(({ id: promptId, text, status, anchorMessageId, attachments }) => ({
            id: promptId,
            text,
            attachmentCount: attachments?.length,
            status: status ?? 'queued',
            anchorMessageId,
          }))
        );
      }
    }
    setQueuedByConversation(next);
  }, []);

  const isRunning = useCallback(
    (conversationId: string, chat?: AIChat<FabrikUIMessage>) =>
      runningPrompts.current.has(conversationId) ||
      chat?.status === 'submitted' ||
      chat?.status === 'streaming',
    []
  );

  const dispatchPrompt = useCallback(
    async function dispatchPrompt(
      conversation: Conversation,
      prompts: PendingPrompt[],
      generation: number,
      isQueued = false,
      retryExisting = false
    ): Promise<void> {
      if (generation !== lifecycleGeneration.current || !prompts.length) return;
      const prompt = prompts[0];
      const text = prompts.map((item) => item.text).join('\n\n');
      const chat = makeChat(conversation, [], prompt.callbacks);
      cancelledRetries.current.delete(conversation.id);
      let selections: PreviewSelectionItem[];
      try {
        selections = aggregateSelection(prompts.map((item) => item.selection ?? []));
      } catch (error) {
        if (useChatStore.getState().selected?.id === conversation.id) {
          useChatStore.getState().handleError(error);
        }
        return;
      }
      const attachments = prompts.flatMap((item) => item.attachments ?? []);
      if (attachments.length > MAX_ATTACHMENTS_PER_MESSAGE) {
        if (useChatStore.getState().selected?.id === conversation.id) {
          useChatStore
            .getState()
            .handleError(
              new Error(
                `Queued messages carry more than ${MAX_ATTACHMENTS_PER_MESSAGE} files. Remove some and try again.`
              )
            );
        }
        return;
      }
      const body = {
        conversationId: conversation.id,
        ...(attachments.length ? { attachments: attachments.map(({ id }) => ({ id })) } : {}),
        ...(selections.length ? { selection: selections } : {}),
        clientMessageId: prompt.id,
        query: text,
        model_type: prompt.model,
        ...(prompt.appId ? { app_id: prompt.appId } : {}),
        ...(prompt.planMode ? { plan_mode: true } : {}),
      };
      failedPrompts.current.delete(conversation.id);
      updateRetry(conversation.id);
      const dispatchId = isQueued ? crypto.randomUUID() : undefined;
      if (dispatchId) {
        const previousUserIds = new Set(
          chat.messages.filter((message) => message.role === 'user').map((message) => message.id)
        );
        const previousAssistantIds = new Set(
          chat.messages
            .filter((message) => message.role === 'assistant')
            .map((message) => message.id)
        );
        const anchorMessageId =
          prompt.anchorMessageId ??
          [...chat.messages].reverse().find((message) => message.role === 'user')?.id;
        setDispatchingByConversation((current) =>
          new Map(current).set(conversation.id, {
            id: dispatchId,
            text,
            attachments,
            anchorMessageId,
            previousUserIds,
            previousAssistantIds,
            isSteering: prompt.status === 'steering',
          })
        );
      }
      runningPrompts.current.add(conversation.id);
      if (useChatStore.getState().selected?.id === conversation.id) setSelectedChat(chat);
      let succeeded = false;
      try {
        for (let retry = 0; retry <= retryLimit; retry += 1) {
          if (
            generation !== lifecycleGeneration.current ||
            cancelledRetries.current.has(conversation.id)
          ) {
            return;
          }
          if (retry > 0 || retryExisting) {
            if (retry > 0) updateRetry(conversation.id, { attempt: retry, maxRetries: retryLimit });
            chat.clearError();
            if (useChatStore.getState().selected?.id === conversation.id) {
              useChatStore.getState().dismissError();
            }
            await new Promise<void>((resolve) => window.setTimeout(resolve, retry * 250));
            if (
              generation !== lifecycleGeneration.current ||
              cancelledRetries.current.has(conversation.id)
            ) {
              return;
            }
          }
          let requestFailed = false;
          let requestError: unknown;
          try {
            if (retry > 0 || retryExisting) {
              await runChatRequest(chat, () => chat.regenerate({ body }));
            } else {
              await runChatRequest(chat, () =>
                chat.sendMessage(
                  {
                    id: prompt.id,
                    role: 'user',
                    parts: [
                      ...attachments.map(({ previewUrl, ...rest }) => ({
                        type: 'data-attachment' as const,
                        data: { ...rest, url: previewUrl },
                      })),
                      ...(text ? [{ type: 'text' as const, text }] : []),
                      ...(selections.length
                        ? [{ type: 'data-preview-selection' as const, data: selections }]
                        : []),
                    ],
                  },
                  { body }
                )
              );
            }
          } catch (error) {
            requestFailed = true;
            requestError = error;
            if (useChatStore.getState().selected?.id === conversation.id) {
              useChatStore.getState().handleError(error);
            }
          }
          if (!requestFailed && chat.status !== 'error') {
            succeeded = true;
            failedPrompts.current.delete(conversation.id);
            updateRetry(conversation.id);
            if (useChatStore.getState().selected?.id === conversation.id) {
              useChatStore.getState().dismissError();
            }
            break;
          }
          if (retry === retryLimit || !isRetryableError(requestError ?? chat.error)) {
            failedPrompts.current.set(conversation.id, {
              ...prompt,
              text,
              selection: selections,
              attachments,
            });
            updateRetry(conversation.id, { attempt: 0, maxRetries: retryLimit, failed: true });
            break;
          }
        }
      } finally {
        runningPrompts.current.delete(conversation.id);
        if (dispatchId) {
          setDispatchingByConversation((current) => {
            if (current.get(conversation.id)?.id !== dispatchId) return current;
            const next = new Map(current);
            next.delete(conversation.id);
            return next;
          });
        }
        if (generation === lifecycleGeneration.current) {
          const next = pendingPrompts.current.get(conversation.id) || [];
          if (succeeded && next.length) {
            pendingPrompts.current.delete(conversation.id);
            void dispatchPrompt(conversation, next, generation, true);
          }
          refreshQueuedMessages();
        }
      }
    },
    [lifecycleGeneration, makeChat, refreshQueuedMessages, setSelectedChat, retryLimit, updateRetry]
  );

  const submit = useCallback(
    async (conversation: Conversation, prompt: PendingPrompt, generation: number) => {
      const chat = makeChat(conversation, [], prompt.callbacks);
      chat.clearError();
      useChatStore.getState().dismissError();
      if (isRunning(conversation.id, chat) || pendingPrompts.current.get(conversation.id)?.length) {
        const queue = pendingPrompts.current.get(conversation.id) || [];
        queue.push(prompt);
        pendingPrompts.current.set(conversation.id, queue);
        refreshQueuedMessages();
        return;
      }
      await dispatchPrompt(conversation, [prompt], generation);
    },
    [dispatchPrompt, isRunning, makeChat, refreshQueuedMessages]
  );

  const retryLast = useCallback(() => {
    const conversation = useChatStore.getState().selected;
    const prompt = conversation && failedPrompts.current.get(conversation.id);
    if (!conversation || !prompt || isRunning(conversation.id, getChat(conversation.id))) return;
    void dispatchPrompt(conversation, [prompt], lifecycleGeneration.current, false, true);
  }, [dispatchPrompt, getChat, isRunning, lifecycleGeneration]);

  const runContinuation = useCallback(
    async (conversation: Conversation, run: () => Promise<void>) => {
      const generation = lifecycleGeneration.current;
      const chat = getChat(conversation.id);
      runningPrompts.current.add(conversation.id);
      let succeeded = false;
      try {
        if (chat) await runChatRequest(chat, run);
        else await run();
        succeeded = Boolean(chat && chat.status !== 'error');
      } finally {
        runningPrompts.current.delete(conversation.id);
        if (succeeded && generation === lifecycleGeneration.current) {
          const next = pendingPrompts.current.get(conversation.id) || [];
          if (next.length) {
            pendingPrompts.current.delete(conversation.id);
            void dispatchPrompt(conversation, next, generation, true);
            refreshQueuedMessages();
          }
        }
      }
    },
    [dispatchPrompt, getChat, lifecycleGeneration, refreshQueuedMessages]
  );

  const removeQueuedMessage = useCallback(
    (id: string) => {
      const conversationId = useChatStore.getState().selected?.id;
      if (!conversationId) return;
      const queue = pendingPrompts.current.get(conversationId);
      if (!queue) return;
      const remaining = queue.filter((prompt) => prompt.id !== id);
      if (remaining.length) pendingPrompts.current.set(conversationId, remaining);
      else pendingPrompts.current.delete(conversationId);
      refreshQueuedMessages();
    },
    [refreshQueuedMessages]
  );

  const steerQueuedMessage = useCallback(
    async (id: string) => {
      const { selected, tenantId } = useChatStore.getState();
      if (!selected || !tenantId) return;
      const queue = pendingPrompts.current.get(selected.id);
      const prompt = queue?.find((item) => item.id === id);
      if (!queue || !prompt) return;
      const chat = getChat(selected.id);
      const running = isRunning(selected.id, chat);
      queue.forEach((item) => {
        item.status = 'queued';
      });
      if (running) {
        prompt.status = 'steering';
        prompt.anchorMessageId = [...(chat?.messages ?? [])]
          .reverse()
          .find((message) => message.role === 'user')?.id;
      }
      pendingPrompts.current.set(selected.id, [prompt, ...queue.filter((item) => item.id !== id)]);
      refreshQueuedMessages();
      if (!running) return;
      try {
        await requestAssistantSteer(tenantId, selected.id);
      } catch (error) {
        prompt.status = 'queued';
        refreshQueuedMessages();
        if (useChatStore.getState().selected?.id === selected.id) {
          useChatStore.getState().handleError(error);
        }
      }
    },
    [getChat, isRunning, refreshQueuedMessages]
  );

  const clear = useCallback(
    (conversationId: string) => {
      pendingPrompts.current.delete(conversationId);
      failedPrompts.current.delete(conversationId);
      cancelledRetries.current.add(conversationId);
      updateRetry(conversationId);
      refreshQueuedMessages();
    },
    [refreshQueuedMessages, updateRetry]
  );

  const clearAll = useCallback(() => {
    pendingPrompts.current.clear();
    runningPrompts.current.clear();
    failedPrompts.current.clear();
    cancelledRetries.current.clear();
    setQueuedByConversation(new Map());
    setDispatchingByConversation(new Map());
    setRetryByConversation(new Map());
  }, []);

  const selectedDispatch = selectedId ? dispatchingByConversation.get(selectedId) : undefined;
  const selectedRetry = selectedId ? retryByConversation.get(selectedId) : undefined;
  return {
    queuedMessages: selectedId ? queuedByConversation.get(selectedId) || [] : [],
    dispatchingMessage: selectedDispatch
      ? { ...selectedDispatch, status: 'sending' as const }
      : undefined,
    retryAttempt: selectedRetry?.attempt || 0,
    maxRetries: selectedRetry?.maxRetries ?? retryLimit,
    canRetry: Boolean(selectedRetry?.failed),
    retryLast,
    isRunning,
    submit,
    runContinuation,
    removeQueuedMessage,
    steerQueuedMessage,
    clear,
    clearAll,
  };
}
